import Loading from '@/components/common/Loading/Loading'
import NoItems from '@/components/common/NoItems/NoItems'
import ScrollToTop from '@/components/common/ScrollToTop/ScrollToTop'
import { ArticleModel } from '@/types/api'
import ISearchResultSettings from '@/types/ISearchResultSettings'
import { ResultView } from '@/types/ResultView'
import { dynamicSort } from '@/utils/helpers'
import { useDeviceType, DeviceType } from '@/utils/hooks/useDeviceType'
import React, { useEffect } from 'react'
import GalleryView from '../GalleryView/GalleryView'
import ListView from '../ListView/ListView'
import ResultBar from '../ResultBar/ResultBar'
import { usePrevious } from '@/utils/hooks/usePrevious'
import { useScrollTo } from '@/utils/hooks/useScrollTo'
import useAnalyticsManager from '@/utils/hooks/useAnalyticsManager'
import SearchMetaData from '@/types/SearchMetaData'
import WheelSearchStep from '@/types/WheelSearchStep'

type Props = {
  isFetching: boolean,
  items: ArticleModel[] | undefined,
  settings: ISearchResultSettings
  scrollToResult?: boolean
  onSelect?: (item: ArticleModel) => void
  listId: string,
  listName: string
  metaData?: SearchMetaData
  step?: WheelSearchStep
}

const SearchResult = (props: Props) => {
  const deviceType = useDeviceType()
  const [view, setView] = React.useState(ResultView.Gallery)
  const [sort, setSort] = React.useState('')
  const prevIsFetching = usePrevious(props.isFetching)
  const { scrollTo, scrollToRef } = useScrollTo()
  const { viewProductList } = useAnalyticsManager()

  const handleOnChangeView = (view: ResultView) => {
    setView(view)
  }

  /**
   * Get view based on view type
   */
  const getView = (items: ArticleModel[]) => {
    switch (view) {
      case ResultView.List:
        return (
          <ListView
            items={items}
            settings={props.settings}
            onSelect={props.onSelect}
          />
        )
      case ResultView.Gallery:
        return (
          <GalleryView
            items={items}
            listId={props.listId}
            listName={props.listName}
            settings={props.settings}
            onSelect={props.onSelect}
            metaData={props.metaData}
          />
        )
      default:
        return <div>Unknown view...</div>
    }
  }

  const onChangeSort = (sortType: string) => {
    setSort(sortType)
  }

  /**
   * Sort items
   */
  const sortedItems = React.useMemo(() => {
    if (!sort) {
      return props.items || []
    }

    if (!props.items) {
      return []
    }

    return [...props.items].sort(dynamicSort(sort))
  }, [props.items, sort])

  useEffect(() => {
    if (deviceType === DeviceType.Mobile) {
      setView(ResultView.Gallery)
    }
  }, [deviceType])

  useEffect(() => {
    if (!props.isFetching && prevIsFetching && props.scrollToResult) {
      scrollTo()
    }
  }, [props.isFetching, prevIsFetching, scrollTo, props.scrollToResult])

  useEffect(() => {
    if (!props.isFetching && props.items && props.items.length > 0) {
      viewProductList(props.items, props.listId, props.listName)
    }
  }, [props.isFetching, props.items])

  return (
    <div ref={scrollToRef}>
      {props.isFetching && (
        <Loading />
      )}
      {!props.isFetching && props.items && (
        <ResultBar
          view={view}
          onChangeView={handleOnChangeView}
          sort={sort}
          onChangeSort={onChangeSort}
          step={props.step}
        />
      )}
      {!props.isFetching &&
        getView(sortedItems)
      }
      {props.items && sortedItems.length === 0 && (
        <NoItems />
      )}
      <ScrollToTop />
    </div>
  )
}

export default SearchResult